import { useEffect, useMemo, useRef, useState } from "react";
import CptSearchBar from "./CptSearchBar";
import ProviderList from "./ProviderList";
import ProviderDetail from "./ProviderDetail";
import AddProviderForm, {
  AddProviderFormValues,
} from "./AddProviderForm";
import {
  DEFAULT_UNMATCHED_CPT_POLICY,
  ServiceProvider,
  UnmatchedCptPolicyId,
} from "../../../utils/integrationConfigData";
import { ProviderIntegrationApi } from "../../../service/Api/ProviderIntegrationApi";
import {
  CptCodeSearchResultApi,
  mapProviderListItemToServiceProvider,
} from "../types/providerIntegrationtypes";
import { useAuditUserId } from "../../../hooks/useAuditUserId";

interface ServiceProvidersPanelProps {
  providers: ServiceProvider[];
  selectedProviderId?: string | null;
  onProviderSelect?: (provider: ServiceProvider) => void;
  authTypes?: import("../types/providerIntegrationtypes").AuthTypeApi[];
  onProviderCreated?: (provider: ServiceProvider) => void;
  onProvidersChanged?: () => void;
  startInAddMode?: boolean;
  totalCodes?: number;
}

const ServiceProvidersPanel = ({
  providers,
  selectedProviderId,
  onProviderSelect,
  authTypes = [],
  onProviderCreated,
  onProvidersChanged,
  startInAddMode = false,
  totalCodes = 0,
}: ServiceProvidersPanelProps) => {
  const [searchMessage, setSearchMessage] = useState("");
  const [detailTab, setDetailTab] = useState("summary");
  const [cptImportNonce, setCptImportNonce] = useState(0);
  const [unmatchedPolicy, setUnmatchedPolicy] = useState<UnmatchedCptPolicyId>(
    DEFAULT_UNMATCHED_CPT_POLICY,
  );
  const [isAddingProvider, setIsAddingProvider] = useState(startInAddMode);
  const [localProviders, setLocalProviders] = useState<ServiceProvider[]>(
    providers,
  );
  const skipSummaryResetRef = useRef(false);

  const auditUserId = useAuditUserId();

  useEffect(() => {
    setLocalProviders(providers);
    if (providers.length === 0) {
      setIsAddingProvider(true);
    }
  }, [providers]);

  useEffect(() => {
    if (!localProviders.length) return;
    const stillVisible = localProviders.some(
      (p) => p.id === selectedProviderId,
    );
    if (!stillVisible && !isAddingProvider) {
      onProviderSelect?.(localProviders[0]);
    }
  }, [localProviders, selectedProviderId, onProviderSelect, isAddingProvider]);

  useEffect(() => {
    setSearchMessage("");
    setCptImportNonce(0);
    if (skipSummaryResetRef.current) {
      skipSummaryResetRef.current = false;
      return;
    }
    setDetailTab("summary");
  }, [selectedProviderId]);

  const selectedProvider =
    localProviders.find((provider) => provider.id === selectedProviderId) ||
    localProviders[0] ||
    null;

  const defaultProviderLabel = useMemo(() => {
    const preferred = localProviders[0];
    return preferred?.shortName || preferred?.name || "default";
  }, [localProviders]);

  const handleCptMatchSelect = (match: CptCodeSearchResultApi) => {
    const provider = localProviders.find(
      (p) => Number(p.id) === match.providerId,
    );
    if (!provider) {
      setSearchMessage(
        `No provider found in the list for code “${match.cptCode}”.`,
      );
      return;
    }
    skipSummaryResetRef.current = true;
    setIsAddingProvider(false);
    setSearchMessage("");
    setDetailTab("cpt");
    onProviderSelect?.(provider);
  };

  const handleDetailTabChange = (tabId: string) => {
    setDetailTab(tabId);
    if (tabId !== "cpt") {
      setCptImportNonce(0);
    }
  };

  const handleAddProvider = () => {
    setSearchMessage("");
    setIsAddingProvider(true);
  };

  const handleCancelAdd = () => {
    // Keep Add form open when there are no providers yet
    if (localProviders.length === 0) return;
    setIsAddingProvider(false);
  };

  const handleSubmitAdd = async (values: AddProviderFormValues) => {
    try {
      const createdBy = auditUserId;

      const created = await ProviderIntegrationApi.createProvider({
        providerName: values.name.trim(),
        providerCode: values.code.trim().toUpperCase(),
        description: values.description.trim() || undefined,
        status: values.status,
        subscriptionKey: values.subscriptionKey.trim(),
        createdBy,
      });
      const mapped = mapProviderListItemToServiceProvider(
        created,
        created.description,
      );
      setIsAddingProvider(false);
      setDetailTab("summary");
      onProviderCreated?.(mapped);
      onProviderSelect?.(mapped);
      setSearchMessage(
        `Provider “${mapped.shortName}” was added. Configure CRD, DTR, and PAS next.`,
      );
      onProvidersChanged?.();
    } catch {
      setSearchMessage("Unable to add provider. Check the code is unique and try again.");
    }
  };

  const handleSelectProvider = (provider: ServiceProvider) => {
    if (isAddingProvider) {
      setIsAddingProvider(false);
    }
    onProviderSelect?.(provider);
  };

  return (
    <div className="ic-providers-panel">
      <CptSearchBar
        totalCodes={totalCodes}
        onSelectMatch={handleCptMatchSelect}
      />

      {searchMessage && (
        <div className="ic-inline-message" role="status">
          {searchMessage}
        </div>
      )}

      <div className="ic-providers-workspace">
        <ProviderList
          providers={localProviders}
          selectedProviderId={
            isAddingProvider ? null : selectedProvider?.id
          }
          onSelect={handleSelectProvider}
          onAddProvider={handleAddProvider}
        />
        {isAddingProvider ? (
          <AddProviderForm
            onCancel={handleCancelAdd}
            onSubmit={handleSubmitAdd}
          />
        ) : (
          <ProviderDetail
            provider={selectedProvider}
            activeTab={detailTab}
            onTabChange={handleDetailTabChange}
            cptImportNonce={cptImportNonce}
            defaultProviderLabel={defaultProviderLabel}
            unmatchedPolicy={unmatchedPolicy}
            onUnmatchedPolicyChange={setUnmatchedPolicy}
            authTypes={authTypes}
            onProvidersChanged={onProvidersChanged}
          />
        )}
      </div>
    </div>
  );
};

export default ServiceProvidersPanel;
